import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Grid,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  CircularProgress,
  TextField,
  InputAdornment,
  Stack,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../components/atoms/Button';
import axios from 'axios';
import useSnackbar from '../../../hooks/useSnackbar';
import AddIcon from '@mui/icons-material/Add';

interface RequestToolModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  technicianId: string;
}

interface ToolRequest {
  toolId: string;
  identifiers: string[];
  technicianId?: string;
  quantity: number;
  comment: string;
}

interface Tool {
  _id: string;
  name: string;
  description?: string;
  image?: string;
  code?: string;
  active: boolean;
}

interface Technician {
  _id: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  position?: string;
  status?: string;
}

const RequestToolModal: React.FC<RequestToolModalProps> = ({
  open,
  onClose,
  onSave,
  technicianId,
}) => {
  const [showSecondRequest, setShowSecondRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // Technician states
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [technicianPage, setTechnicianPage] = useState(1);
  const [technicianTotalPages, setTechnicianTotalPages] = useState(1);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [technicianSearch, setTechnicianSearch] = useState('');

  // First Request
  const [toolId1, setToolId1] = useState('');
  const [toolName1, setToolName1] = useState('');
  const [identifier1, setIdentifier1] = useState('');
  const [quantity1, setQuantity1] = useState(1);
  const [reason1, setReason1] = useState('');
  const [comment1, setComment1] = useState('');

  // Second Request
  const [toolId2, setToolId2] = useState('');
  const [toolName2, setToolName2] = useState('');
  const [toolType2, setToolType2] = useState('');
  const [identifier2, setIdentifier2] = useState('');
  const [quantity2, setQuantity2] = useState(1);
  const [reason2, setReason2] = useState('');
  const [comment2, setComment2] = useState('');

  // Tool list states
  const [tools1, setTools1] = useState<Tool[]>([]);
  const [tools2, setTools2] = useState<Tool[]>([]);
  const [toolPage1, setToolPage1] = useState(1);
  const [toolPage2, setToolPage2] = useState(1);
  const [toolTotalPages1, setToolTotalPages1] = useState(1);
  const [toolTotalPages2, setToolTotalPages2] = useState(1);
  const [loadingTools1, setLoadingTools1] = useState(false);
  const [loadingTools2, setLoadingTools2] = useState(false);
  const [toolSearch1, setToolSearch1] = useState('');
  const [toolSearch2, setToolSearch2] = useState('');
  const [identifiers, setIdentifiers] = useState<Array<string>>([]);

  const getToken = () => {
    const storedData = localStorage.getItem('authData');
    if (storedData) {
      const parsed = JSON.parse(storedData);
      return parsed.token;
    }
    return null;
  };

  // API Calls
  const fetchToolsFromAPI = async (
    pageNum: number,
    search = '',
    limit = 100,
  ) => {
    try {
      const token = getToken();
      let url = `${process.env.REACT_APP_SERVER_API_BASE_URL}/api/v1/admin/tools/list?limit=${limit}&page=${pageNum}`;

      if (search) {
        url += `&search=${search}`;
      }
      url += `&sortField=name&sortOrder=ASC`;

      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data.success) {
        return response.data;
      }
      throw new Error('Failed to fetch tools');
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  const fetchTechniciansFromAPI = async (
    pageNum: number,
    search = '',
    limit = 100,
  ) => {
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_SERVER_API_BASE_URL}/api/v1/admin/technician/list?limit=${limit}&page=${pageNum}`;

      const response = await axios.post(
        url,
        { search },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200 && response.data.status) {
        return response.data;
      }
      throw new Error('Failed to fetch technicians');
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  // Fetch technicians
  const fetchTechnicians = useCallback(async (pageNum: number, search = '') => {
    setLoadingTechnicians(true);
    try {
      const response = await fetchTechniciansFromAPI(pageNum, search);
      const newTechnicians = response?.data || [];
      const totalPages = response?.pagination?.totalPages || 1;

      if (pageNum === 1) {
        setTechnicians(newTechnicians);
      } else {
        setTechnicians((prev) => [...prev, ...newTechnicians]);
      }

      setTechnicianTotalPages(totalPages);
      setTechnicianPage(pageNum);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    } finally {
      setLoadingTechnicians(false);
    }
  }, []);

  // Fetch tools for first dropdown
  const fetchTools1 = useCallback(async (pageNum: number, search = '') => {
    setLoadingTools1(true);
    try {
      const response = await fetchToolsFromAPI(pageNum, search);
      const newTools = response?.data || [];
      const totalPages = response?.pagination?.totalPages || 1;

      if (pageNum === 1) {
        setTools1(newTools);
      } else {
        setTools1((prev) => [...prev, ...newTools]);
      }

      setToolTotalPages1(totalPages);
      setToolPage1(pageNum);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoadingTools1(false);
    }
  }, []);

  // Initial load when modal opens
  useEffect(() => {
    if (open) {
      setTechnicians([]);
      setTools1([]);
      setTools2([]);
      setTechnicianPage(1);
      setToolPage1(1);
      setToolPage2(1);
      setTechnicianSearch('');
      setToolSearch1('');
      setToolSearch2('');
      setToolId1('');
      setToolId2('');
      fetchTechnicians(1, '');
      fetchTools1(1, '');
    }
  }, [open]);

  const handleToolChange1 = (event: SelectChangeEvent) => {
    const value = event.target.value;
    console.log('Setting tool ID 1:', value);
    setToolId1(value);
    const selectedTool = tools1.find((tool) => tool._id === value);
    if (selectedTool) {
      setToolName1(selectedTool.name);
      setIdentifier1(selectedTool.code || '');
    }
  };

  const validateForm = (): boolean => {
    // Basic required fields
    if (!toolId1 || !quantity1) {
      showSnackbar(
        'Please fill all required fields for first tool request',
        'error',
      );
      return false;
    }

    // Identifier validation
    if (identifiers.length > 0 && identifiers.length !== Number(quantity1)) {
      showSnackbar('Number of identifiers must be equal to quantity', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        toolId: toolId1,
        identifiers: identifiers,
        technicianId: technicianId,
        quantity: quantity1,
        comment: comment1 || '',
      };

      console.log('payload to be sent:', payload);

      const token = getToken();

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_API_BASE_URL}/api/v1/admin/assignTool`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log('Response:', response.data);

      showSnackbar('Tool request submitted successfully!');

      // Reset form
      setToolId1('');
      setToolName1('');
      setIdentifier1('');
      setQuantity1(1);
      setReason1('');
      setComment1('');
      setToolId2('');
      setToolName2('');
      setToolType2('');
      setIdentifier2('');
      setQuantity2(1);
      setReason2('');
      setComment2('');
      setShowSecondRequest(false);

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error submitting request:', error);
      showSnackbar('Failed to submit request. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Simple Tool Dropdown 1 - Fixed
  const renderToolDropdown1 = () => {
    return (
      <FormControl fullWidth>
        <Select
          value={toolId1}
          onChange={handleToolChange1}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return <span style={{ color: '#666' }}>Select Tool</span>;
            }
            const selectedTool = tools1.find((t) => t._id === selected);
            return selectedTool?.name || '';
          }}
          sx={{
            height: '55px',
            backgroundColor: '#181818',
            color: '#fff',
            border: '1px solid #D9D9D9',
            borderRadius: '9px',
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: '#fff' },
          }}
        >
          <MenuItem value="" disabled>
            <em>Select Tool</em>
          </MenuItem>
          {tools1.map((tool) => (
            <MenuItem key={tool._id} value={tool._id}>
              {tool.name} {tool.code ? `(${tool.code})` : ''}
            </MenuItem>
          ))}
          {loadingTools1 && (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{ color: '#E31E25', mr: 1 }} />
              Loading...
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  };

  const handleDelete = (itemToDelete: any) => {
    setIdentifiers((prev) => prev.filter((item) => item !== itemToDelete));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '30px',
          backgroundColor: '#181818',
          border: '1px solid #E31E25',
        },
      }}
    >
      {SnackbarComponent}
      <DialogTitle
        sx={{
          fontSize: '22px',
          fontWeight: 'bold',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#181818',
          padding: '16px',
          borderBottom: '1px solid #E31E25',
        }}
      >
        Assign Tool
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
        <Grid container spacing={2} className="pt-5">
          {/* First Request */}
          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Tool Name
            </Typography>
            {renderToolDropdown1()}
          </Grid>
          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Quantity
            </Typography>
            <input
              className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              placeholder="Enter Quantity"
              type="number"
              min="1"
              value={quantity1}
              onChange={(e) => setQuantity1(parseInt(e.target.value) || 1)}
              onWheel={(e) => (e.target as HTMLElement).blur()}
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault();
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Identifier
            </Typography>

            <TextField
              fullWidth
              placeholder="Enter Identifier"
              value={identifier1}
              onChange={(e) => setIdentifier1(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#161616',
                  borderRadius: '9px',
                  height: '55px',
                  color: '#fff',

                  '& fieldset': {
                    borderColor: '#D9D9D9',
                  },
                  '&:hover fieldset': {
                    borderColor: '#D9D9D9',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#D9D9D9',
                  },
                },
                '& input::placeholder': {
                  color: '#5a5e68',
                  opacity: 1,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={() => {
                      if (identifier1.trim()) {
                        setIdentifiers((prev: Array<string>) => [
                          ...prev,
                          identifier1.trim(),
                        ]);
                        setIdentifier1('');
                      }
                    }}
                  >
                    {identifiers.length !== Number(quantity1) && (
                      <IconButton>
                        <AddIcon sx={{ color: '#fff' }} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography gutterBottom sx={{ color: '#fff', fontSize: '14px' }}>
              Comment
            </Typography>
            <input
              className="border border-[#D9D9D9] rounded-[9px] w-full bg-transparent h-[55px] focus:outline-none focus:ring-0 placeholder:text-[#5a5e68] pl-3 text-white"
              placeholder="Enter Comment"
              value={comment1}
              onChange={(e) => setComment1(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            {!!identifiers?.length && (
              <Grid item xs={12}>
                <Typography sx={{ color: '#fff', fontSize: '14px', mb: 1 }}>
                  Selected Identifiers
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mb: 2, rowGap: 1 }}
                >
                  {identifiers.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      onDelete={() => handleDelete(item)}
                      deleteIcon={<CloseIcon />}
                      sx={{
                        backgroundColor: 'transparent',
                        color: '#fff',
                        border: '1px solid #D9D9D9',
                        '& .MuiChip-deleteIcon': {
                          color: '#fff',
                          '&:hover': { color: 'red' },
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: '#181818',
          padding: '16px',
          display: 'flex',
          justifyContent: 'end',
          borderTop: '1px solid #E31E25',
        }}
      >
        <PrimaryButton
          title={loading ? 'Saving...' : 'Save'}
          onClick={handleSubmit}
          disabled={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default RequestToolModal;
